// Master Apps · deck de apresentação do portfólio.
//
// Tokens e regras do deck "Dados, IA e Inovação — Desenvolvimentos", portado do
// PowerPoint que a área usa hoje. As medidas NÃO são arbitrárias: saíram do
// próprio .pptx exportado em PDF (página de 1440 × 810 pt, 16:9), então cada
// constante daqui é a coordenada real do slide original. Mantê-las é o que faz
// a tela e o PDF impresso saírem idênticos ao material aprovado.
//
// Arquivo é TypeScript puro (sem DOM, sem React) para ser testável no mesmo
// `bun test` do monorepo — a apresentação em si vive em
// `components/screens/presentation-screen.tsx`.
import { APP_TYPES, type App } from '@/lib/catalog-config';

/** Largura do slide, em px = pt do PowerPoint original (16:9). */
export const DECK_W = 1440;
/** Altura do slide, em px = pt do PowerPoint original (16:9). */
export const DECK_H = 810;

/**
 * Paleta do deck. É o vermelho do PowerPoint (#C50905), NÃO o `NOC_RED`
 * (#C8102E) do produto: o material impresso precisa bater com os slides que já
 * circulam na diretoria, então a apresentação é a única tela que foge do
 * acento do tema.
 */
export const DECK = {
  red: '#C50905',
  /** Faixa escura na borda esquerda do painel vermelho. */
  redEdge: '#8A0504',
  /** Número gigante em marca d'água no rodapé do painel. */
  redWatermark: '#A20504',
  /** Rótulos pequenos sobre o vermelho (rosa claro, não branco puro). */
  redLabel: '#FFB8B6',
  ink: '#1A1A1A',
  inkMuted: '#6B6B6B',
  panel: '#F4F4F4',
  white: '#FFFFFF',
} as const;

/** Geometria do slide, em px. Coordenadas absolutas dentro do canvas 1440×810. */
export const DECK_GEO = {
  /** Painel vermelho à esquerda. */
  railW: 430,
  /** Faixa vermelha escura colada na borda esquerda. */
  edgeW: 8,
  /** Recuo do texto dentro do painel. */
  railPad: 57,
  /**
   * Régua branca que separa o título do bloco de metadados.
   *
   * Subiu de 410 para 320 porque havia ~200px de espaço morto entre o fim do
   * título e a régua, enquanto embaixo a pilha de identidade descia até cobrir
   * a marca d'água do número da plataforma — visto na projeção, com o "Custo
   * mensal" escrito por cima do "06". A folga estava sobrando em cima e
   * faltando embaixo. Pior caso conferido: título de TRÊS linhas começa em 98 e
   * termina em ~243, ainda com 77px de respiro até a régua.
   */
  ruleY: 320,
  ruleW: 80,
  ruleH: 4.5,
  /** Coluna de conteúdo à direita do painel. */
  contentX: 470,
  contentW: 930,
  /**
   * Bloco cinza "SOBRE A PLATAFORMA".
   *
   * Cresceu 20,5px (de 568..752,5 para 550..755) para caber a TERCEIRA linha do
   * retorno esperado: com duas linhas, texto de projeto real vinha cortado na
   * projeção. Os 18px que faltavam saíram da área da captura, que perdeu 15px
   * de altura — a imagem é ajustada por `objectFit: contain` e não distorce,
   * enquanto o texto cortado some justamente no fim, que é onde está a
   * conclusão da frase.
   */
  aboutY: 550,
  aboutH: 205,
  /** Área reservada à captura de tela da aplicação. */
  shotY: 53,
  shotH: 485,
  /** Rodapé (assinatura à esquerda, logo à direita). */
  footerY: 774,
} as const;

export interface DeckStatus {
  key: string;
  /** Rótulo em caixa alta, como aparece na pílula do slide. */
  label: string;
  color: string;
  /** Manchete do card no slide de visão. */
  headline: string;
  /** Frase de apoio do card no slide de visão. */
  blurb: string;
}

/**
 * Ordem canônica dos status no deck: do que já gera resultado para o que ainda
 * não gera. É a ordem dos cards da visão e das pílulas do filtro.
 *
 * `pausado` entra em PENÚLTIMO, entre `manutencao` e `depreciado`, e o motivo é
 * a própria régua da lista: a cauda é a faixa do que NÃO gera resultado hoje.
 * Vem depois de `manutencao` porque manutenção está no ar entregando (com
 * ressalva), e pausado não entrega nada. E vem ANTES de `depreciado` porque a
 * parada é reversível — é o último degrau em que o projeto ainda volta, e
 * `depreciado` é o terminal de onde não se volta. Pôr os dois parados juntos no
 * fim também é o que faz o slide ler direito: a diretoria vê, lado a lado, o
 * que está congelado e o que já morreu, que são decisões diferentes.
 */
export const DECK_STATUS_ORDER = [
  'prod',
  'homolog',
  'dev',
  'manutencao',
  'pausado',
  'depreciado',
] as const;

export const DECK_STATUS: Record<string, DeckStatus> = {
  prod: {
    key: 'prod',
    label: 'PRODUÇÃO',
    color: '#1F893E',
    headline: 'Em uso pelo negócio',
    blurb: 'Plataformas estáveis, em operação contínua e gerando resultado.',
  },
  homolog: {
    key: 'homolog',
    label: 'HOMOLOGAÇÃO',
    color: '#C8840F',
    headline: 'Em validação',
    blurb: 'Sistemas em teste com usuários-chave antes do go-live.',
  },
  dev: {
    key: 'dev',
    label: 'DESENVOLVIMENTO',
    color: '#1F4FA8',
    headline: 'Em construção',
    blurb: 'Projetos em andamento com entregas previstas para os próximos meses.',
  },
  manutencao: {
    key: 'manutencao',
    label: 'MANUTENÇÃO',
    color: '#C2410C',
    headline: 'Em evolução',
    blurb: 'Plataformas no ar recebendo correções e melhorias contratadas.',
  },
  /**
   * O ROXO (#9A348C) não é escolha de gosto: as outras cinco cores já ocupam
   * verde, âmbar, azul, laranja e cinza, e para quem não distingue vermelho de
   * verde o verde, o âmbar e o laranja colapsam todos na mesma faixa amarelada
   * — sobrar "mais um quente" seria a sexta cor indistinguível de três outras.
   * O valor foi CONFERIDO por simulação (Viénot) de protanopia, deuteranopia e
   * tritanopia: em cada uma delas a menor distância ΔE do roxo para as cinco
   * cores existentes é MAIOR que a menor distância que já existe entre as
   * próprias cinco (o pior par de hoje é âmbar × laranja). Ou seja, quem
   * distingue a paleta atual distingue esta também. O tom é escuro de
   * propósito (contraste 6,5:1 com o branco da pílula, acima do verde de
   * produção) e o croma fica na mesma faixa dos demais — roxo neon destoaria
   * do material impresso.
   */
  pausado: {
    key: 'pausado',
    label: 'PAUSADO',
    color: '#9A348C',
    headline: 'Em espera',
    blurb: 'Projetos iniciados e parados por decisão, prontos para retomar.',
  },
  depreciado: {
    key: 'depreciado',
    label: 'DEPRECIADO',
    color: '#6B6B6B',
    headline: 'Fora de operação',
    blurb: 'Plataformas descontinuadas, mantidas apenas para consulta histórica.',
  },
};

/**
 * Status do deck para uma aplicação. Cadastro com status desconhecido cai em
 * "produção" — mesmo default do `STATUS_TYPES` do catálogo, para o slide nunca
 * sair sem pílula (era o defeito das plataformas 44 e 47 do deck manual).
 */
export function deckStatusOf(app: Pick<App, 'status'>): DeckStatus {
  return DECK_STATUS[app.status] ?? (DECK_STATUS.prod as DeckStatus);
}

export interface DeckStatusCount extends DeckStatus {
  count: number;
  /** Participação no portfólio, em percentual (0 a 100). */
  share: number;
}

/**
 * Contagem por status, na ordem canônica e SEM as faixas vazias — o slide de
 * visão mostra um card por status que existe de fato, então um portfólio só de
 * produção e homologação rende dois cards, não cinco vazios.
 */
export function deckCounts(apps: readonly Pick<App, 'status'>[]): DeckStatusCount[] {
  return DECK_STATUS_ORDER.map((key) => {
    const status = DECK_STATUS[key] as DeckStatus;
    const count = apps.filter((a) => deckStatusOf(a).key === key).length;
    return { ...status, count, share: apps.length > 0 ? (count / apps.length) * 100 : 0 };
  }).filter((s) => s.count > 0);
}

/** Áreas solicitantes distintas atendidas pelo portfólio. */
export function deckAreaCount(apps: readonly Pick<App, 'team'>[]): number {
  return new Set(apps.map((a) => a.team.trim()).filter(Boolean)).size;
}

// ── Período do deck ─────────────────────────────────────────────────────────
//
// O deck é "Desenvolvimentos <período>": por padrão o ANO corrente, com a
// opção de um intervalo de meses (inclusive atravessando anos). O critério de
// pertencimento é a ÚLTIMA ATUALIZAÇÃO da aplicação (`lastUpdate`): é o único
// carimbo de atividade obrigatório do cadastro — `deployedAt` é opcional e
// `createdAt` marca quando a ficha foi criada, não quando se trabalhou nela.

export const DECK_MONTHS_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

/** Um mês de calendário. `month` é 1–12 (como pessoas contam, não como o JS). */
export interface DeckMonth {
  year: number;
  month: number;
}

export type DeckPeriod =
  | { mode: 'year'; year: number }
  | { mode: 'range'; start: DeckMonth; end: DeckMonth };

/** Chave ordenável AAAA*12+M — aritmética de meses sem Date/fuso. */
const monthKey = (m: DeckMonth) => m.year * 12 + (m.month - 1);

/** Intervalo invertido (início depois do fim) vale pelos mesmos meses: troca. */
export function normalizeDeckPeriod(period: DeckPeriod): DeckPeriod {
  if (period.mode === 'year') return period;
  if (monthKey(period.start) <= monthKey(period.end)) return period;
  return { mode: 'range', start: period.end, end: period.start };
}

/**
 * O carimbo ISO (`YYYY-MM-DD…`) cai dentro do período? Datas ausentes ou fora
 * do padrão ficam FORA de qualquer período: o deck é um recorte temporal, e
 * quem não tem data não pertence a recorte nenhum (aparece no padrão "ano
 * corrente"? não — conserta-se o cadastro, não o filtro).
 */
export function deckPeriodContains(period: DeckPeriod, iso: string | null | undefined): boolean {
  if (!iso) return false;
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return false;
  if (period.mode === 'year') return year === period.year;
  const p = normalizeDeckPeriod(period) as Extract<DeckPeriod, { mode: 'range' }>;
  const key = year * 12 + (month - 1);
  return key >= monthKey(p.start) && key <= monthKey(p.end);
}

/** A aplicação pertence ao período do deck (pela última atualização)? */
export function appInDeckPeriod(app: Pick<App, 'lastUpdate'>, period: DeckPeriod): boolean {
  return deckPeriodContains(period, app.lastUpdate);
}

/** Anos com alguma atividade cadastrada, do mais recente para o mais antigo. */
export function deckAvailableYears(apps: readonly Pick<App, 'lastUpdate'>[]): number[] {
  const years = new Set<number>();
  for (const app of apps) {
    const m = /^(\d{4})-/.exec(app.lastUpdate ?? '');
    if (m) years.add(Number(m[1]));
  }
  return [...years].sort((a, b) => b - a);
}

export interface DeckPeriodTitle {
  /** Linha principal da capa: "Desenvolvimentos 2026" ou só "Desenvolvimentos". */
  title: string;
  /** Segunda linha (só no modo intervalo): "Janeiro a Junho de 2026". */
  sub?: string;
}

/**
 * Título da capa por período. Ano: uma linha, como o deck original. Intervalo:
 * "Desenvolvimentos" + o intervalo por extenso embaixo — "Janeiro a Junho de
 * 2026" no mesmo ano, "Outubro de 2025 a Março de 2026" cruzando anos.
 */
export function deckPeriodTitle(period: DeckPeriod): DeckPeriodTitle {
  if (period.mode === 'year') return { title: `Desenvolvimentos ${period.year}` };
  const p = normalizeDeckPeriod(period) as Extract<DeckPeriod, { mode: 'range' }>;
  const start = DECK_MONTHS_PT[p.start.month - 1];
  const end = DECK_MONTHS_PT[p.end.month - 1];
  if (p.start.year === p.end.year) {
    return {
      title: 'Desenvolvimentos',
      sub:
        p.start.month === p.end.month
          ? `${start} de ${p.start.year}`
          : `${start} a ${end} de ${p.start.year}`,
    };
  }
  return {
    title: 'Desenvolvimentos',
    sub: `${start} de ${p.start.year} a ${end} de ${p.end.year}`,
  };
}

/** Rótulo compacto de ano(s) para rodapés e kickers: "2026" ou "2025–2026". */
export function deckPeriodYears(period: DeckPeriod): string {
  if (period.mode === 'year') return String(period.year);
  const p = normalizeDeckPeriod(period) as Extract<DeckPeriod, { mode: 'range' }>;
  return p.start.year === p.end.year ? String(p.start.year) : `${p.start.year}–${p.end.year}`;
}

/** "01", "02"… "47". Numeração do slide, sempre com dois dígitos no mínimo. */
export function deckSlideNumber(index: number): string {
  return String(index + 1).padStart(2, '0');
}

/**
 * Parâmetros do título no painel vermelho.
 *
 * `charRatio` é a largura média do caractere em em no peso 800 da fonte do
 * produto (Montserrat/Gotham), que é bem mais larga que a Calibri do PowerPoint
 * original — daí o título não poder herdar os 50px do .pptx sem conta.
 */
export const DECK_TITLE = {
  max: 50,
  min: 26,
  /** Largura útil dentro do painel: largura total − recuo − folga à direita. */
  maxWidth: 356,
  charRatio: 0.62,
} as const;

/**
 * Degraus do total de caracteres: vence o primeiro cujo limite o nome
 * ultrapassa (por isso a lista desce do maior para o menor). Nome mais curto
 * que o último degrau fica no corpo cheio.
 */
const DECK_TITLE_LENGTH_STEPS: readonly { readonly over: number; readonly size: number }[] = [
  { over: 44, size: 32 },
  { over: 32, size: 38 },
  { over: 22, size: 44 },
];

/**
 * Corpo do título da plataforma. Dois limites, e vence o menor:
 *
 *  · a PALAVRA mais longa precisa caber na largura do painel — é ela que
 *    sangra pela borda ("Desenvolvimento" a 44px estourava), não o total;
 *  · o total de caracteres cede em degraus, senão um nome comprido ocupa
 *    quatro linhas e encosta na régua.
 */
export function deckTitleSize(name: string): number {
  const trimmed = name.trim();
  const longestWord = trimmed.split(/\s+/).reduce((max, word) => Math.max(max, word.length), 0);

  const byWord =
    longestWord > 0 ? DECK_TITLE.maxWidth / (longestWord * DECK_TITLE.charRatio) : DECK_TITLE.max;
  const n = trimmed.length;
  const byLength = DECK_TITLE_LENGTH_STEPS.find((step) => n > step.over)?.size ?? DECK_TITLE.max;

  return Math.round(Math.max(DECK_TITLE.min, Math.min(DECK_TITLE.max, byWord, byLength)));
}

/**
 * Título do documento durante a impressão. O Chrome usa o `document.title` como
 * nome sugerido no "Salvar como PDF", então é ele que define o nome do arquivo
 * que o usuário recebe.
 */
export function deckDocumentTitle(at: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `Plataformas Masterboi · ${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

// ── Painel vermelho e card cinza do slide da plataforma ─────────────────────
//
// AQUI SÓ ENTRAM CONSTANTES, e o motivo é concreto: o gate de cobertura do
// `bunfig.toml` é POR ARQUIVO (90% de linhas E de funções), este módulo está em
// 100% e o teste que o cobre (`apps/api/tests/web-deck.test.ts`) é de outra
// frente. Função nova sem teste aqui reprovaria `bun test` inteiro; constante é
// executada no import, então não mexe no número. A derivação (quais campos a
// aplicação tem, quantas linhas cada um ocupa, onde o status acaba caindo) vive
// em `presentation-screen.tsx`, que `coveragePathIgnorePatterns` já exclui por
// ser `.tsx`. As COORDENADAS ficam aqui porque são do mesmo canvas 1440×810 do
// `DECK_GEO`, e quem for mexer no slide precisa dos dois blocos lado a lado.

/**
 * Empilhamento dos campos de IDENTIDADE no painel vermelho, na ordem em que a
 * diretoria pediu: área solicitante, dono do projeto, responsável e esforço.
 *
 * Os quatro são OPCIONAIS no cadastro (só `owner` é obrigatório, e 42 das 44
 * aplicações não têm nenhum campo de projeto), então a pilha é construída com
 * os campos QUE EXISTEM — campo vazio não vira rótulo órfão nem buraco no meio
 * da coluna. Por isso as coordenadas daqui são um orçamento, não posições
 * fixas: `presentation-screen.tsx` empilha de cima para baixo a partir de `top`
 * e só o STATUS e os CARIMBOS têm âncora mínima (ver `statusMinY` e
 * `DECK_RAIL_META`), para o slide de quem não tem projeto sair exatamente onde
 * saía antes.
 *
 * A conta do pior caso (os quatro campos preenchidos, com o nome do setor
 * ocupando duas linhas):
 *
 *   área 16 + 2×24 = 64 · dono 16 + 18 = 34 · responsável 34 · esforço 16 + 22
 *   = 38, mais três vãos de 12 → 206px, que é exatamente o `budget` abaixo.
 *
 * E 206 é o que sobra de `bottom − top` (324) depois de reservar o status
 * (18 + 36) e a linha de carimbos (18 + 18), com dois vãos de 14 entre os três
 * blocos. Pilha que passe disso perde uma linha por campo, do último para o
 * primeiro — nome de pessoa cortado com reticências é um mal menor do que a
 * linha de custo mensal impressa por cima da assinatura Masterboi.
 */
/**
 * Peças do painel que entram na conta do `budget`. Ficam fora do objeto porque
 * `as const` não deixa uma chave referenciar a irmã — e o orçamento TEM de ser
 * derivado: quem mover o rodapé ou trocar a altura da pílula sem recalcular na
 * mão publica um número que não descreve mais o painel.
 */
// Sobe junto com a regua (410 -> 320): a pilha comeca 90px mais alta, e com
// isso os carimbos de baixo saem de cima da marca d'agua nas aplicacoes que
// tem os quatro campos de projeto preenchidos.
const RAIL_TOP = 346;
const RAIL_BOTTOM = 760;
const RAIL_BLOCK_GAP = 14;
const RAIL_PILL_SHORT = 36;
/** Do topo de um rótulo ao topo do valor (ou da pílula) que vem sob ele. */
const RAIL_LABEL_H = 16;
const RAIL_STATUS_LABEL_H = 18;
/**
 * Do rótulo do carimbo ao valor, e do valor à sua base — 18 nos dois casos
 * (corpo 15 com entrelinha 1,2 dá exatamente 18px de linha).
 */
const RAIL_META_H = 18;
/**
 * Segunda linha de carimbos ("Acessos no mês"): rótulo e valor, 36px — a mesma
 * altura da primeira linha. Chegou a ter uma sub-linha de usuários ativos (51px);
 * saiu quando a métrica virou "dias do mês com acesso", que cabe num valor só.
 */
const RAIL_META2_H = RAIL_META_H * 2;

export const DECK_RAIL_STACK = {
  /** Primeiro rótulo da pilha, logo abaixo da régua branca (410 + 4,5). */
  top: RAIL_TOP,
  /** Do topo do rótulo ao topo do valor. */
  labelH: RAIL_LABEL_H,
  /** Vão entre dois campos da pilha. */
  gap: 12,
  /** Vão entre os três blocos do painel (pilha, status, carimbos). */
  blockGap: RAIL_BLOCK_GAP,
  /** Do rótulo "Status atual" ao topo da pílula. */
  statusLabelH: RAIL_STATUS_LABEL_H,
  /** Largura útil: `railW − railPad` menos a folga da direita (430 − 57 − 30). */
  width: 343,
  /** Corpo do PRIMEIRO campo — é ele que faz o papel de manchete do painel. */
  leadSize: 20,
  /** Corpo dos demais campos de texto. */
  size: 15,
  /** Corpo do total de horas: número, e por isso um pouco maior que o texto. */
  esforcoSize: 18,
  /** Entrelinha dos valores, em múltiplos do corpo. */
  lineRatio: 1.2,
  /**
   * Largura média do caractere em em, no peso 600/700 da fonte do produto. Serve
   * para ESTIMAR linhas sem medir no DOM — o painel é desenhado em coordenadas
   * absolutas, então quem decide a altura precisa da resposta antes de pintar.
   */
  charRatio: 0.5,
  /** Teto de linhas de um valor: na terceira o painel vira ficha. */
  maxLines: 2,
  /**
   * Orçamento da pilha inteira, em px: o vão do painel menos o que o status e
   * as DUAS linhas de carimbos reservam, com os vãos entre os quatro blocos.
   *
   * Dá 246 hoje. A segunda linha de carimbos (acessos, 36px + um vão de 14)
   * custou 50px do orçamento, e ele ainda cobre com folga o pior caso de 206
   * da pilha descrito acima — a régua subir de 410 para 320 é o que abriu esse
   * espaço; sem isso a linha nova teria de roubar linhas dos campos de projeto.
   */
  budget:
    RAIL_BOTTOM -
    RAIL_TOP -
    (RAIL_STATUS_LABEL_H + RAIL_PILL_SHORT) -
    RAIL_META_H * 2 -
    RAIL_META2_H -
    RAIL_BLOCK_GAP * 3,
  /**
   * Onde "Status atual" fica quando a pilha não empurra nada — o caso de 42 das
   * 44 aplicações, que ficam com o painel idêntico ao de antes deste bloco.
   */
  statusMinY: 524,
  /** Pílula cheia (pilha curta) e pílula baixa (pilha comprida). */
  pillTall: 43.5,
  pillShort: RAIL_PILL_SHORT,
  /** Último pixel que os carimbos podem ocupar (a assinatura começa em 774). */
  bottom: RAIL_BOTTOM,
} as const;

/**
 * Linha dos CARIMBOS no painel: "Última atualização" e "Custo mensal · Railway"
 * lado a lado, em duas colunas.
 *
 * Eram duas linhas empilhadas (614 e 674), e o custo terminava em y≈710 — a
 * 56px do rodapé. Emparelhar os dois devolveu os ~60px que a pilha de
 * identidade precisava: os valores são curtos ("17/08/2026", "R$ 0,00" — o
 * custo é exibido em REAIS, convertido pela cotação do dia, e "R$" é ainda mais
 * estreito que o "US$" que havia aqui antes) e o
 * rótulo mais longo ocupa 145 dos 178px da coluna direita.
 *
 * `labelY` é um MÍNIMO, não uma âncora: a linha só desce quando a pilha de
 * identidade e o status realmente chegam até ela, o que hoje acontece em 2 das
 * 44 aplicações. Nas outras 42 ela sai exatamente em 614, como sempre saiu —
 * um "Custo mensal" que subisse e descesse a cada avanço piscaria na projeção.
 */
export const DECK_RAIL_META = {
  labelY: 614,
  /** Do topo do rótulo ao topo do valor. */
  valueH: RAIL_META_H,
  /** Início da coluna direita (a esquerda começa no `railPad`). */
  col2X: 232,
  col1W: 165,
  col2W: 178,
  /**
   * SEGUNDA linha de carimbos: "Acessos · 30 dias" (sessões no Clerk) com o
   * número em corpo 15 e, embaixo, "N usuários ativos" em corpo menor.
   *
   * Fica SÓ NA COLUNA ESQUERDA, e o motivo é a marca d'água: com o corpo 170
   * ela ocupa o canto inferior direito do painel a partir de y≈666 e de
   * x≈220 (medido no navegador; varia com os algarismos — "01" começa em 269,
   * "04" em 218), exatamente onde cairia uma coluna direita desta linha (a
   * primeira linha termina em 650; esta começa em 664). A esquerda passa ao
   * lado: o valor mais largo ("31/31", corpo 15) acaba antes de 120.
   * A linha entra logo abaixo da primeira, no mesmo vão dos blocos, e como a
   * primeira ela só DESCE quando a pilha de identidade empurra — nas fichas
   * sem projeto sai sempre em 664, e termina em 700, a 74px do rodapé.
   */
  row2Gap: RAIL_BLOCK_GAP,
} as const;

/**
 * Card cinza "Sobre a plataforma": a descrição da aplicação e, abaixo dela, o
 * retorno esperado do projeto.
 *
 * O card é o mesmo retângulo de sempre (`DECK_GEO.aboutY` + `aboutH` = 568 a
 * 752,5); o que mudou é o miolo. Saiu a régua de metadados (Tipo, Responsável,
 * Criticidade e Stack), que ocupava a faixa de 694 a 740 e repetia no rodapé do
 * card o que o painel vermelho já diz na lateral.
 *
 * O bloco do retorno é ANCORADO na base (`bottom`) e a descrição fica com o que
 * sobra em cima: assim o rótulo "Retorno esperado" cai sempre na mesma altura,
 * em todas as plataformas que o têm, em vez de flutuar conforme o tamanho da
 * descrição. Sem retorno cadastrado o card não ganha rótulo órfão — a descrição
 * herda o card inteiro e passa de 3 para 6 linhas, que é mais do que as 4 que a
 * régua de metadados lhe deixava.
 */
export const DECK_ABOUT = {
  /** Recuo interno do card. */
  padX: 31,
  /** Rótulo "Sobre a plataforma". */
  labelY: 588,
  /** Primeira linha da descrição. */
  descY: 610,
  /** Última linha de base utilizável dentro do card (a borda cai em 752,5). */
  bottom: 740,
  /** Largura útil: `contentW − 2 × padX` (930 − 62). */
  width: 868,
  /** Entrelinha da descrição, em múltiplos do corpo. */
  descLineRatio: 1.44,
  /** Do topo do rótulo do retorno ao topo do texto. */
  labelH: 16,
  /** Vão entre a descrição e o bloco do retorno. */
  gap: 12,
  retornoSize: 13,
  retornoLineRatio: 1.44,
  /** Linhas reservadas ao retorno esperado. */
  // Tres linhas desde que o card cresceu: com duas, retorno de projeto real
  // vinha cortado na projecao.
  retornoLines: 3,
  /**
   * Quanto uma MAIÚSCULA conta na contagem de caracteres do retorno. Medido
   * junto do `DECK_RETORNO_MAX`: o mesmo parágrafo em caixa alta cabe em 205
   * caracteres onde a versão em caixa mista cabe em 255 — 1,24× mais largo. Com
   * 1,3 por maiúscula, os dois casos cruzam o limite praticamente no ponto em
   * que a caixa de fato transborda, e um texto normal (uma maiúscula por frase)
   * não muda de contagem.
   */
  maiusculaPeso: 1.3,
} as const;

/**
 * Quantos caracteres do "Retorno esperado" cabem nas DUAS linhas do card.
 *
 * MEDIDO no navegador, não estimado. O texto foi crescendo por busca binária
 * dentro da caixa real do slide (868px de largura, corpo 13 na fonte do
 * produto, entrelinha 1,44) até o `scrollHeight` passar de duas linhas:
 *
 *   parágrafo de prosa em português ......... 255 e 258 caracteres
 *   o mesmo parágrafo em CAIXA ALTA ......... 205
 *   texto só de letras estreitas ("mínimo") . 328
 *   texto só de letras largas ("WMS WOW") ... 183
 *
 * Com DUAS linhas o número era 250. O card cresceu e passou a reservar TRÊS
 * linhas, então o teto subiu proporcionalmente e foi CONFERIDO no navegador:
 * 360 caracteres de prosa renderizam inteiros, sem o aviso de corte.
 *
 * O cadastro continua aceitando 2000 caracteres: este é o limite do SLIDE, e é
 * o número que a área usa para resumir os textos que já estão no banco. Texto
 * maior NÃO quebra a apresentação — o card corta em duas linhas e o próprio
 * rótulo avisa, ao lado, que o texto completo está na ficha da aplicação. O que
 * não pode acontecer é o corte silencioso.
 */
export const DECK_RETORNO_MAX = 360;

/** Rótulo do grupo de quem ainda não tem área solicitante cadastrada. */
export const DECK_SEM_AREA = 'Sem área definida';

/**
 * Área solicitante usada para agrupar e ordenar o deck.
 *
 * É `projetoArea` — a ÁREA QUE PEDIU a plataforma, escolhida da base de setores
 * do TOTVS — e não `team`, que é o time que construiu. A lista do lobby
 * agrupava por `team` e o resultado era um grupo só: 48 das 49 fichas têm
 * "Desenvolvimento" ali, porque é sempre o mesmo time que entrega.
 */
export function deckAreaOf(app: Pick<App, 'projetoArea'>): string {
  return app.projetoArea?.trim() || DECK_SEM_AREA;
}

/**
 * Ordem PADRÃO do deck: por área solicitante, e dentro dela por nome.
 *
 * Quem não tem área cai num grupo único NO FIM — o deck abre pelo que está
 * atribuído a uma área do negócio, e o que falta cadastrar fica junto, visível,
 * em vez de espalhado no meio da sequência.
 *
 * `localeCompare` com 'pt-BR' para "ÁREA" e "AREA" caírem no mesmo lugar e
 * "Ó" não ir parar depois de "Z".
 */
export function deckOrdemPorArea<T extends Pick<App, 'projetoArea' | 'name'>>(
  apps: readonly T[],
): T[] {
  return [...apps].sort((a, b) => {
    const areaA = deckAreaOf(a);
    const areaB = deckAreaOf(b);
    if (areaA !== areaB) {
      // O grupo sem área é sempre o último, qualquer que seja o alfabeto.
      if (areaA === DECK_SEM_AREA) return 1;
      if (areaB === DECK_SEM_AREA) return -1;
      return areaA.localeCompare(areaB, 'pt-BR');
    }
    return a.name.localeCompare(b.name, 'pt-BR');
  });
}

/**
 * Aplica a ordem que o apresentador montou arrastando, com a ordem por área
 * como base.
 *
 * Plataforma que NÃO está na ordem salva vai para o fim, na ordem por área: é o
 * caso da ficha cadastrada depois que alguém arrastou. Somem-la seria perder
 * cadastro do deck em silêncio; encaixá-la "na área certa" no meio de uma
 * sequência que a pessoa montou à mão seria mexer no trabalho dela sem pedir.
 * No fim ela aparece, e mover custa um arrasto.
 *
 * Ordem salva vazia = ninguém arrastou ainda = ordem por área pura.
 */
export function deckAplicarOrdem<T extends Pick<App, 'id' | 'projetoArea' | 'name'>>(
  apps: readonly T[],
  ordem: readonly string[],
): T[] {
  const padrao = deckOrdemPorArea(apps);
  if (ordem.length === 0) return padrao;
  const posicao = new Map(ordem.map((id, i) => [id, i]));
  const arrastadas = padrao
    .filter((a) => posicao.has(a.id))
    .sort((a, b) => (posicao.get(a.id) ?? 0) - (posicao.get(b.id) ?? 0));
  const novas = padrao.filter((a) => !posicao.has(a.id));
  return [...arrastadas, ...novas];
}

/**
 * Move `idArrastado` para a posição de `idAlvo`, devolvendo a lista de ids na
 * ordem nova — a operação que o arrasto do lobby precisa.
 *
 * Recebe a lista INTEIRA (o recorte visível não basta): a ordem salva precisa
 * continuar valendo para as plataformas que um filtro escondeu no momento do
 * arrasto, senão filtrar e arrastar apagaria a posição das outras.
 */
export function deckMoverNaOrdem(
  ids: readonly string[],
  idArrastado: string,
  idAlvo: string,
): string[] {
  const de = ids.indexOf(idArrastado);
  const para = ids.indexOf(idAlvo);
  if (de < 0 || para < 0 || de === para) return [...ids];
  const next = [...ids];
  next.splice(de, 1);
  next.splice(para, 0, idArrastado);
  return next;
}

// ── Ordenação por coluna da tabela do lobby ─────────────────────────────────
//
// A tabela do lobby deixa clicar no cabeçalho para ordenar (por nome, área,
// situação…), mas essa ordenação é SÓ DE LEITURA: ela não é salva e não é a
// ordem do deck. A ordem que vale nos slides continua sendo a manual/por área
// (`deckAplicarOrdem`); quando o apresentador arrasta uma linha, a tela desliga
// a ordenação por coluna e a ordem manual volta a aparecer — senão o arrasto
// pareceria não ter efeito, porque a coluna reordenaria tudo de novo.

export type DeckColunaOrdenavel =
  | 'nome'
  | 'area'
  | 'tipo'
  | 'situacao'
  | 'criticidade'
  | 'responsavel'
  | 'acessos'
  | 'atualizada';

export interface DeckOrdenacao {
  col: DeckColunaOrdenavel;
  /** 1 = crescente, -1 = decrescente. */
  dir: 1 | -1;
}

/** Criticidade da mais alta para a mais baixa — a ordem em que se lê a coluna. */
const CRITICIDADE_RANK: Record<string, number> = { alta: 0, media: 1, baixa: 2 };

type AppOrdenavel = Pick<
  App,
  | 'name'
  | 'projetoArea'
  | 'type'
  | 'status'
  | 'criticality'
  | 'owner'
  | 'acessosDiasMes'
  | 'lastUpdate'
>;

/**
 * Chave de comparação de uma ficha para a coluna. `null` = "sem valor" e vai
 * para o FIM nas duas direções: uma coluna de acessos ordenada do maior para o
 * menor não pode abrir com os "—" de quem nunca foi medido.
 */
function chaveDaColuna(app: AppOrdenavel, col: DeckColunaOrdenavel): string | number | null {
  switch (col) {
    case 'nome':
      return app.name;
    case 'area':
      return deckAreaOf(app) === DECK_SEM_AREA ? null : deckAreaOf(app);
    case 'tipo':
      return APP_TYPES[app.type]?.label ?? app.type;
    case 'situacao':
      return DECK_STATUS_ORDER.indexOf(deckStatusOf(app).key as (typeof DECK_STATUS_ORDER)[number]);
    case 'criticidade':
      return CRITICIDADE_RANK[app.criticality] ?? null;
    case 'responsavel':
      return app.owner.trim() || null;
    case 'acessos':
      return typeof app.acessosDiasMes === 'number' ? app.acessosDiasMes : null;
    case 'atualizada':
      return app.lastUpdate || null;
    default:
      return null;
  }
}

/**
 * Cópia de `apps` ordenada pela coluna. Sem ordenação (`null`) devolve a lista
 * como veio — a ordem manual. Empate mantém a ordem de entrada (sort estável),
 * então ordenar por situação preserva a sequência manual dentro de cada faixa.
 */
export function deckOrdenarPorColuna<T extends AppOrdenavel>(
  apps: readonly T[],
  ordenacao: DeckOrdenacao | null,
): T[] {
  if (!ordenacao) return [...apps];
  const { col, dir } = ordenacao;
  return [...apps].sort((a, b) => {
    const ka = chaveDaColuna(a, col);
    const kb = chaveDaColuna(b, col);
    if (ka === null && kb === null) return 0;
    if (ka === null) return 1;
    if (kb === null) return -1;
    if (typeof ka === 'number' && typeof kb === 'number') return (ka - kb) * dir;
    return String(ka).localeCompare(String(kb), 'pt-BR') * dir;
  });
}
